import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCheckDuplicateDocument, useDocuments, useCreateDocument, useDeleteDocument, useUpdateDocumentNotes, useDownloadDocument, useAuthenticatedBlobUrl, useDocumentVersionCount, useDocumentVersionCounts } from '../hooks/useQueries';
import { usePopupMenu } from '../hooks/usePopupMenu';
import DataTable from './Layout/DataTable';
import PopupMenu from './Popup/PopupMenu';
import Modal from './Popup/Modal';
import DuplicateDocumentModal from './Popup/DuplicateDocumentModal';
import VersionHistoryModal from './Popup/VersionHistoryModal';
import Toast from './Popup/Toast';
import { formatDateTime } from '../utils/dateUtils';
import './DocumentsDisplay.css';

function DocumentsDisplay({ parentId, parentType = 'item', showHeader = true, maxHeight = '500px' }) {
    // State management
    const [uploadModal, setUploadModal] = useState({
        show: false,
        file: null,
        notes: '',
        isNewVersion: false,
        existingDocumentId: null,
        acceptMimeType: '*',
        baseFilename: ''
    });
    const [isUploadDragActive, setIsUploadDragActive] = useState(false);
    const [previewModal, setPreviewModal] = useState({ show: false, document: null });
    const [editNotesModal, setEditNotesModal] = useState({ show: false, document: null, notes: '' });
    const [deleteModal, setDeleteModal] = useState({ show: false, document: null, versionCount: 0 });
    const [sortConfig, setSortConfig] = useState({ key: 'uploaded_at', direction: 'desc' });
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [duplicateModal, setDuplicateModal] = useState({ show: false, existingDocumentId: null, filename: '' });
    const [versionHistoryModal, setVersionHistoryModal] = useState({ show: false, document: null });
    const popupMenu = usePopupMenu();
    const queryClient = useQueryClient();

    // Data fetching
    const { data: { documents = [] } = {}, isLoading: loading } = useDocuments(parentId);
    const createDocumentMutation = useCreateDocument();
    const duplicateCheckQuery = useCheckDuplicateDocument(parentId, uploadModal.file?.name || '', { enabled: false });
    const deleteDocumentMutation = useDeleteDocument();
    const updateDocumentNotesMutation = useUpdateDocumentNotes();
    const downloadDocument = useDownloadDocument();

    const versionCounts = useDocumentVersionCounts(
        documents.map((d) => d.id),
        { enabled: documents.length > 0 }
    );

    const versionCountQuery = useDocumentVersionCount(deleteModal.document?.id, {
        enabled: deleteModal.show && !!deleteModal.document?.id,
        staleTime: 1000 * 60,
    });

    useEffect(() => {
        if (!deleteModal.show) return;
        const count = versionCountQuery.data?.version_count;
        if (Number.isFinite(count)) {
            setDeleteModal(prev => {
                if (!prev.show) return prev;
                if (prev.versionCount === count) return prev;
                return { ...prev, versionCount: count };
            });
        }
    }, [deleteModal.show, versionCountQuery.data?.version_count]);

    const renameFileWithTimestamp = (file) => {
        if (!file || typeof file.name !== 'string') return file;

        const name = file.name;
        const dotIndex = name.lastIndexOf('.');
        const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
        const ext = dotIndex > 0 ? name.slice(dotIndex) : '';
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        const newName = `${base}-${stamp}${ext}`;

        try {
            return new File([file], newName, { type: file.type, lastModified: file.lastModified });
        } catch (e) {
            return file;
        }
    };

    // Authenticated blob URL for preview
    const { url: previewBlobUrl, isLoading: previewLoading, error: previewError } = useAuthenticatedBlobUrl(
        previewModal.document?.container_name,
        previewModal.document?.blob_name,
        { enabled: previewModal.show && !!previewModal.document }
    );

    // Helper functions
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (mimeType) => {
        if (mimeType.startsWith('image/')) return 'bi-file-image';
        if (mimeType === 'application/pdf') return 'bi-file-pdf';
        return 'bi-file-earmark';
    };

    const isPreviewable = (mimeType) => {
        return mimeType.startsWith('image/') || mimeType === 'application/pdf';
    };

    const showToast = useCallback((message, type = 'success') => {
        setToast({ show: true, message, type });
    }, []);

    const handleCloseToast = () => {
        setToast({ show: false, message: '', type: 'success' });
    };

    // Upload functionality
    const handleUploadClick = () => {
        setUploadModal({
            show: true,
            file: null,
            notes: '',
            isNewVersion: false,
            existingDocumentId: null,
            acceptMimeType: '*',
            baseFilename: ''
        });
    };

    const handleSelectedFile = (file) => {
        if (!file) return;

        console.log('Selected file:', {
            name: file.name,
            type: file.type,
            size: file.size,
            isFileObject: file instanceof File,
            constructor: file.constructor.name
        });

        // Check file size (50MB max)
        const maxSize = 50 * 1024 * 1024; // 50MB in bytes
        if (file.size > maxSize) {
            showToast('File size must be less than 50MB', 'error');
            return;
        }

        if (
            uploadModal.isNewVersion &&
            uploadModal.acceptMimeType &&
            uploadModal.acceptMimeType !== '*' &&
            file.type &&
            file.type !== uploadModal.acceptMimeType
        ) {
            showToast(`Please select a file of type ${uploadModal.acceptMimeType}`, 'error');
            return;
        }

        setUploadModal(prev => ({ ...prev, file }));
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        handleSelectedFile(file);

        // Allow selecting the same file again
        event.target.value = '';
    };

    const handleUploadDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsUploadDragActive(true);
    };

    const handleUploadDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsUploadDragActive(true);
    };

    const handleUploadDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsUploadDragActive(false);
    };

    const handleUploadDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsUploadDragActive(false);

        const file = e.dataTransfer?.files?.[0];
        handleSelectedFile(file);
    };

    const handleConfirmUpload = async () => {
        if (!uploadModal.file) {
            console.log('No file in uploadModal:', uploadModal);
            return;
        }

        console.log('Uploading file:', {
            name: uploadModal.file.name,
            size: uploadModal.file.size,
            type: uploadModal.file.type,
            parentId,
            notes: uploadModal.notes,
            fileObject: uploadModal.file,
            isFileObject: uploadModal.file instanceof File,
            fileConstructor: uploadModal.file.constructor.name
        });

        try {
            if (parentType !== 'item') {
                throw new Error('Document upload is only supported for items');
            }

            // If uploading a new version, skip duplicate check and force version upload.
            if (uploadModal.isNewVersion) {
                await createDocumentMutation.mutateAsync({
                    itemId: parentId,
                    file: uploadModal.file,
                    notes: uploadModal.notes,
                    isNewVersion: true,
                    existingDocumentId: uploadModal.existingDocumentId,
                });

                showToast('New version uploaded successfully');
                setUploadModal({
                    show: false,
                    file: null,
                    notes: '',
                    isNewVersion: false,
                    existingDocumentId: null,
                    acceptMimeType: '*',
                    baseFilename: ''
                });
                return;
            }

            // Check duplicates before uploading
            const duplicateResult = await duplicateCheckQuery.refetch();
            if (duplicateResult?.data?.exists && duplicateResult?.data?.document?.id) {
                setDuplicateModal({
                    show: true,
                    existingDocumentId: duplicateResult.data.document.id,
                    filename: uploadModal.file.name,
                });
                return;
            }

            await createDocumentMutation.mutateAsync({
                itemId: parentId,
                file: uploadModal.file,
                notes: uploadModal.notes
            });

            showToast('Document uploaded successfully');
            setUploadModal({
                show: false,
                file: null,
                notes: '',
                isNewVersion: false,
                existingDocumentId: null,
                acceptMimeType: '*',
                baseFilename: ''
            });
        } catch (error) {
            console.error('Upload error:', error);
            showToast(error.message || 'Failed to upload document', 'error');
        }
    };

    const handleDuplicateAddAsVersion = async () => {
        try {
            await createDocumentMutation.mutateAsync({
                itemId: parentId,
                file: uploadModal.file,
                notes: uploadModal.notes,
                isNewVersion: true,
                existingDocumentId: duplicateModal.existingDocumentId,
            });

            showToast('New version uploaded successfully');
            setDuplicateModal({ show: false, existingDocumentId: null, filename: '' });
            setUploadModal({ show: false, file: null, notes: '' });
        } catch (error) {
            console.error('Upload new version error:', error);
            showToast(error.message || 'Failed to upload new version', 'error');
        }
    };

    const handleDuplicateUploadAsNewDocument = async () => {
        try {
            const renamedFile = renameFileWithTimestamp(uploadModal.file);
            await createDocumentMutation.mutateAsync({
                itemId: parentId,
                file: renamedFile,
                notes: uploadModal.notes,
            });

            showToast('Document uploaded successfully (renamed)');
            setDuplicateModal({ show: false, existingDocumentId: null, filename: '' });
            setUploadModal({ show: false, file: null, notes: '' });
        } catch (error) {
            console.error('Upload renamed document error:', error);
            showToast(error.message || 'Failed to upload document', 'error');
        }
    };

    // Preview functionality
    const handlePreviewClick = useCallback((document) => {
        setPreviewModal({ show: true, document });
        popupMenu.closePopupMenu();
    }, [popupMenu]);

    // Download functionality
    const handleDownload = useCallback(async (document) => {
        try {
            await downloadDocument(document.id, document.filename);
            popupMenu.closePopupMenu();
        } catch (error) {
            showToast(error.message || 'Failed to download document', 'error');
        }
    }, [downloadDocument, popupMenu, showToast]);

    // Delete functionality
    const handleDeleteClick = useCallback((document) => {
        setDeleteModal({ show: true, document, versionCount: 0 });
        popupMenu.closePopupMenu();
    }, [popupMenu]);

    const handleConfirmDelete = async () => {
        try {
            await deleteDocumentMutation.mutateAsync(deleteModal.document.id);
            showToast('Document deleted successfully');
            setDeleteModal({ show: false, document: null, versionCount: 0 });
        } catch (error) {
            showToast(error.message || 'Failed to delete document', 'error');
        }
    };

    // Notes edit functionality
    const handleEditNotesClick = useCallback((document) => {
        setEditNotesModal({ show: true, document, notes: document.notes || '' });
        popupMenu.closePopupMenu();
    }, [popupMenu]);

    const handleConfirmEditNotes = async () => {
        try {
            await updateDocumentNotesMutation.mutateAsync({
                id: editNotesModal.document.id,
                notes: editNotesModal.notes
            });
            // Invalidate documents data to refresh the display
            queryClient.invalidateQueries(['documents', 'by-item', parentId]);
            showToast('Notes updated successfully');
            setEditNotesModal({ show: false, document: null, notes: '' });
        } catch (error) {
            showToast(error.message || 'Failed to update notes', 'error');
        }
    };

    // Sort documents based on current sort configuration
    const sortedDocuments = useMemo(() => {
        if (!documents.length) return [];
        
        const sorted = [...documents].sort((a, b) => {
            const { key, direction } = sortConfig;
            let aVal = a[key];
            let bVal = b[key];
            
            // Handle different data types
            if (key === 'uploaded_at') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (key === 'file_size') {
                aVal = parseInt(aVal) || 0;
                bVal = parseInt(bVal) || 0;
            } else if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = (bVal || '').toLowerCase();
            }
            
            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sorted;
    }, [documents, sortConfig]);

    // Handle sort changes
    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // DataTable configuration
    const columns = useMemo(() => [
        {
            key: 'filename',
            label: 'File',
            sortable: true,
            render: (document) => (
                <div className="file-cell">
                    <i className={`${getFileIcon(document.mime_type)} file-icon ${document.mime_type.startsWith('image/') ? 'image' : document.mime_type === 'application/pdf' ? 'pdf' : 'default'}`}></i>
                    <span className="filename-wrap">
                        <span
                            className="filename"
                            title={document.filename}
                        >
                            {document.filename.length > 40 ? `${document.filename.substring(0, 40)}...` : document.filename}
                        </span>
                        {(() => {
                            const versionCount = versionCounts.byId?.[document.id];
                            const inferredMulti = (document.version_number || 1) > 1;
                            const hasMulti = Number.isFinite(versionCount) ? versionCount > 1 : inferredMulti;
                            if (!hasMulti) return null;

                            const badgeCount = Number.isFinite(versionCount) ? versionCount : document.version_number;
                            const title = Number.isFinite(versionCount)
                                ? `${versionCount} versions available`
                                : 'Multiple versions available';

                            return (
                                <span
                                    className={`version-badge ${!Number.isFinite(versionCount) && versionCounts.isLoading ? 'loading' : ''}`}
                                    title={title}
                                    aria-label={title}
                                >
                                    v{badgeCount}
                                </span>
                            );
                        })()}
                    </span>
                </div>
            )
        },
        {
            key: 'file_size',
            label: 'Size',
            sortable: true,
            render: (document) => (
                <span className="size-cell">{formatFileSize(document.file_size)}</span>
            )
        },
        {
            key: 'uploaded_by',
            label: 'Uploaded By',
            sortable: true,
            render: (document) => (
                <span className="uploaded-by-cell">{document.uploaded_by}</span>
            )
        },
        {
            key: 'uploaded_at',
            label: 'Uploaded At',
            sortable: true,
            render: (document) => (
                <span className="date-cell">{formatDateTime(document.uploaded_at)}</span>
            )
        },
        {
            key: 'upload_stage_name',
            label: 'Upload Stage',
            sortable: true,
              render: (document) => (
                <span className="upload-stage-cell">
                    {document.upload_stage_name || 'Stage Deleted'}
                </span>
              )
        },
        {
            key: 'notes',
            label: 'Notes',
            sortable: false,
            render: (document) => (
                <span 
                    className="notes-cell" 
                    title={document.notes || 'No notes'}
                >
                    {document.notes ? (
                        document.notes.length > 50 ? `${document.notes.substring(0, 50)}...` : document.notes
                    ) : (
                        <em>No notes</em>
                    )}
                </span>
            )
        }
    ], [versionCounts.byId, versionCounts.isLoading]);

    // Row click handler
    const handleRowClick = (event, document) => {
        if (popupMenu.popupMenu.item && popupMenu.popupMenu.item.id === document.id) {
            popupMenu.closePopupMenu();
        } else {
            popupMenu.handleItemClick(event, document);
        }
    };

    // PopupMenu items
    const popupMenuItems = useMemo(() => {
        const selectedDocument = popupMenu.popupMenu.item;
        if (!selectedDocument) return [];

        const items = [];

        if (isPreviewable(selectedDocument.mime_type)) {
                items.push({
                    label: 'Preview',
                    icon: <i className="bi bi-eye"></i>,
                    onClick: () => handlePreviewClick(selectedDocument)
                });
        }

            items.push(
                {
                    label: 'Download',
                    icon: <i className="bi bi-download"></i>,
                    onClick: () => handleDownload(selectedDocument)
                },
                {
                    label: 'Upload New Version',
                    icon: <i className="bi bi-clock-history"></i>,
                    onClick: () => {
                        setUploadModal({
                            show: true,
                            file: null,
                            notes: '',
                            isNewVersion: true,
                            existingDocumentId: selectedDocument.id,
                            acceptMimeType: selectedDocument.mime_type || '*',
                            baseFilename: selectedDocument.filename || ''
                        });
                        setIsUploadDragActive(false);
                        popupMenu.closePopupMenu();
                    }
                },
                {
                    label: 'Version History',
                    icon: <i className="bi bi-clock-history"></i>,
                    onClick: () => {
                        setVersionHistoryModal({ show: true, document: selectedDocument });
                        popupMenu.closePopupMenu();
                    }
                },
                {
                    label: 'Edit Notes',
                    icon: <i className="bi bi-pencil"></i>,
                    onClick: () => handleEditNotesClick(selectedDocument)
                },
                {
                    label: 'Delete',
                    icon: <i className="bi bi-trash"></i>,
                    color: '#dc2626',
                    onClick: () => handleDeleteClick(selectedDocument)
                }
            );

        return items;
    }, [popupMenu, handlePreviewClick, handleDownload, handleEditNotesClick, handleDeleteClick]);

    return (
        <div className="documents-display">
            {showHeader && (
                <div className="documents-display-header">
                    <h5>Supporting Documents ({documents.length})</h5>
                    <div className="documents-display-actions">
                        <button 
                            className="btn btn-primary"
                            onClick={handleUploadClick}
                            disabled={createDocumentMutation.isPending}
                        >
                            <i className="bi-upload"></i>
                            Upload Document
                        </button>
                    </div>
                </div>
            )}
            
            <div className="documents-display-table" style={{ maxHeight }}>
                <DataTable
                    columns={columns}
                    data={sortedDocuments}
                    loading={loading}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onRowClick={handleRowClick}
                    emptyMessage="No documents found for this item"
                />
            </div>

            {/* PopupMenu */}
            {popupMenu.popupMenu.show && (
                <PopupMenu
                    x={popupMenu.popupMenu.x}
                    y={popupMenu.popupMenu.y}
                    items={popupMenuItems}
                    onClose={popupMenu.closePopupMenu}
                />
            )}

            {/* Upload Modal */}
            {uploadModal.show && createPortal(
                <Modal
                    title={
                        uploadModal.isNewVersion
                            ? `Upload New Version of ${uploadModal.baseFilename || 'Document'}`
                            : 'Upload Document'
                    }
                    show={uploadModal.show}
                    onCancel={() => setUploadModal({
                        show: false,
                        file: null,
                        notes: '',
                        isNewVersion: false,
                        existingDocumentId: null,
                        acceptMimeType: '*',
                        baseFilename: ''
                    })}
                    onConfirm={handleConfirmUpload}
                    confirmText="Upload"
                    confirmDisabled={!uploadModal.file || createDocumentMutation.isPending}
                    size="md"
                    content={
                        <div className="upload-container">
                            <div 
                                className={`upload-area ${uploadModal.file ? 'has-file' : ''} ${isUploadDragActive ? 'drag-active' : ''}`}
                                onClick={() => document.getElementById('file-input').click()}
                                onDragEnter={handleUploadDragEnter}
                                onDragOver={handleUploadDragOver}
                                onDragLeave={handleUploadDragLeave}
                                onDrop={handleUploadDrop}
                            >
                                <input
                                    id="file-input"
                                    type="file"
                                    accept={uploadModal.acceptMimeType || '*'}
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                {!uploadModal.file ? (
                                    <>
                                        <div className="upload-icon">
                                            <i className="bi-cloud-upload"></i>
                                        </div>
                                        <div className="upload-text">
                                            Click or drag & drop a file
                                        </div>
                                        <div className="upload-text" style={{ fontSize: '12px', color: '#94a3b8' }}>
                                            All file types supported (max 50MB)
                                        </div>
                                    </>
                                ) : (
                                    <div className="file-info">
                                        <i className={`${getFileIcon(uploadModal.file.type)} file-info-icon`}></i>
                                        <div className="file-info-details">
                                            <div className="file-info-name">{uploadModal.file.name}</div>
                                            <div className="file-info-size">{formatFileSize(uploadModal.file.size)}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <div className="notes-edit-container">
                                <label className="notes-edit-label">Notes (optional)</label>
                                <textarea
                                    className="notes-edit-textarea"
                                    placeholder="Add notes about this document..."
                                    value={uploadModal.notes}
                                    onChange={(e) => setUploadModal(prev => ({ ...prev, notes: e.target.value }))}
                                />
                            </div>
                        </div>
                    }
                />,
                document.body
            )}

            {/* Duplicate Document Modal */}
            {duplicateModal.show && createPortal(
                <DuplicateDocumentModal
                    show={duplicateModal.show}
                    filename={duplicateModal.filename}
                    isLoading={createDocumentMutation.isPending}
                    onCancel={() => setDuplicateModal({ show: false, existingDocumentId: null, filename: '' })}
                    onAddAsNewVersion={handleDuplicateAddAsVersion}
                    onUploadAsNewDocument={handleDuplicateUploadAsNewDocument}
                />,
                document.body
            )}

            {versionHistoryModal.show && createPortal(
                <VersionHistoryModal
                    show={versionHistoryModal.show}
                    document={versionHistoryModal.document}
                    onCancel={() => setVersionHistoryModal({ show: false, document: null })}
                    onRestore={() => {
                        // Refresh documents after restore
                        queryClient.invalidateQueries(['documents', 'by-item', parentId]);
                        setVersionHistoryModal({ show: false, document: null });
                    }}
                />,
                document.body
            )}

            {/* Preview Modal */}
            {previewModal.show && createPortal(
                <Modal
                    title="Preview Document"
                    show={previewModal.show}
                    onCancel={() => setPreviewModal({ show: false, document: null })}
                    cancelText="Exit"
                    size="lg"
                    hideConfirm={true}
                    content={
                        <div className="preview-container">
                            <div className="preview-metadata">
                                <div className="preview-metadata-item">
                                    <div className="preview-metadata-label">Filename</div>
                                    <div className="preview-metadata-value">{previewModal.document?.filename}</div>
                                </div>
                                <div className="preview-metadata-item">
                                    <div className="preview-metadata-label">Size</div>
                                    <div className="preview-metadata-value">{formatFileSize(previewModal.document?.file_size)}</div>
                                </div>
                                <div className="preview-metadata-item">
                                    <div className="preview-metadata-label">Uploaded By</div>
                                    <div className="preview-metadata-value">{previewModal.document?.uploaded_by}</div>
                                </div>
                                <div className="preview-metadata-item">
                                    <div className="preview-metadata-label">Uploaded At</div>
                                    <div className="preview-metadata-value">{formatDateTime(previewModal.document?.uploaded_at)}</div>
                                </div>
                            </div>
                            
                            <div className="preview-content">
                                {previewLoading ? (
                                    <div className="preview-loading">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div>Loading preview...</div>
                                    </div>
                                ) : previewError ? (
                                    <div className="preview-error">
                                        <i className="bi-exclamation-triangle"></i>
                                        <div>Preview Error: {previewError.message || 'Unable to load preview'}</div>
                                        <button 
                                            className="btn btn-primary"
                                            onClick={() => handleDownload(previewModal.document)}
                                        >
                                            <i className="bi-download"></i>
                                            Download File
                                        </button>
                                    </div>
                                ) : previewBlobUrl ? (
                                    <>
                                        {previewModal.document?.mime_type.startsWith('image/') ? (
                                            <img 
                                                src={previewBlobUrl} 
                                                alt={previewModal.document.filename}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'block';
                                                }}
                                            />
                                        ) : previewModal.document?.mime_type === 'application/pdf' ? (
                                            <embed 
                                                src={previewBlobUrl}
                                                type="application/pdf"
                                                width="100%"
                                                height="600px"
                                            />
                                        ) : null}
                                        
                                        <div className="preview-error" style={{ display: 'none' }}>
                                            <i className="bi-exclamation-triangle"></i>
                                            <div>Unable to preview this file</div>
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => handleDownload(previewModal.document)}
                                            >
                                                <i className="bi-download"></i>
                                                Download File
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="preview-error">
                                        <i className="bi-exclamation-triangle"></i>
                                        <div>Unable to load preview</div>
                                        <button 
                                            className="btn btn-primary"
                                            onClick={() => handleDownload(previewModal.document)}
                                        >
                                            <i className="bi-download"></i>
                                            Download File
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    }
                />,
                document.body
            )}

            {/* Edit Notes Modal */}
            {editNotesModal.show && createPortal(
                <Modal
                    title="Edit Document Notes"
                    show={editNotesModal.show}
                    onCancel={() => setEditNotesModal({ show: false, document: null, notes: '' })}
                    onConfirm={handleConfirmEditNotes}
                    confirmText="Save"
                    confirmDisabled={updateDocumentNotesMutation.isPending}
                    size="md"
                    content={
                        <div className="notes-edit-container">
                            <div className="notes-edit-label">Document: {editNotesModal.document?.filename}</div>
                            <textarea
                                className="notes-edit-textarea"
                                placeholder="Add notes about this document..."
                                value={editNotesModal.notes}
                                onChange={(e) => setEditNotesModal(prev => ({ ...prev, notes: e.target.value }))}
                            />
                        </div>
                    }
                />,
                document.body
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && createPortal(
                <Modal
                    title="Confirm Delete"
                    show={deleteModal.show}
                    onCancel={() => setDeleteModal({ show: false, document: null, versionCount: 0 })}
                    onConfirm={handleConfirmDelete}
                    confirmText="Delete"
                    confirmBtnClass="btn-danger"
                    confirmDisabled={deleteDocumentMutation.isPending}
                    size="md"
                    content={
                        <div className="delete-confirmation-content">
                            {deleteModal.versionCount > 1 && (
                                <div className="alert alert-danger">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <strong>Warning:</strong> This will delete the document and all {deleteModal.versionCount - 1} previous version(s).
                                </div>
                            )}
                            <div className="alert alert-warning">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <strong>Warning:</strong> This action cannot be undone. This will delete the document and any saved versions.
                            </div>
                            <p>
                                Are you sure you want to delete <strong>{deleteModal.document?.filename}</strong>?
                            </p>
                            <div className="document-details">
                                <div><strong>Size:</strong> {deleteModal.document ? formatFileSize(deleteModal.document.file_size) : 'Unknown'}</div>
                                <div>
                                    <strong>Versions:</strong>{' '}
                                    {versionCountQuery.isLoading ? (
                                        <span className="text-muted">Loading…</span>
                                    ) : versionCountQuery.isError ? (
                                        <span className="text-danger" title={versionCountQuery.error?.message || 'Failed to load version count'}>
                                            Unavailable
                                        </span>
                                    ) : (
                                        deleteModal.versionCount
                                    )}
                                </div>
                                <div><strong>Uploaded:</strong> {deleteModal.document?.uploaded_at ? formatDateTime(deleteModal.document.uploaded_at) : 'Unknown'}</div>
                                <div><strong>Uploaded by:</strong> {deleteModal.document?.uploaded_by || 'Unknown'}</div>
                            </div>
                        </div>
                    }
                />,
                document.body
            )}

            {/* Toast */}
            {toast.show && createPortal(
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={handleCloseToast}
                />,
                document.body
            )}
        </div>
    );
}

export default DocumentsDisplay;